import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { orderAPI } from '../../utils/api';
import { Eye, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const STATUS_ALB = {
  pending: 'Në pritje',
  processing: 'Duke u procesuar',
  shipped: 'Dërguar',
  delivered: 'Dorëzuar',
  cancelled: 'Anuluar',
  refunded: 'Rimbursuar'
};

const BADGE_MAP = {
  pending: 'badge-gold',
  processing: 'badge-blue',
  shipped: 'badge-blue',
  delivered: 'badge-green',
  cancelled: 'badge-red',
  refunded: 'badge-gray'
};

export default function OrdersPage() {
  const [selectedOrder, setSelectedOrder] = useState(null);

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['user-orders'],
    queryFn: () => orderAPI.getAll().then(r => r.data),
  });

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', paddingTop: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div style={{ minHeight: '100vh', paddingTop: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: '2rem', color: 'var(--green-deep)' }}>Porositë e Mia</h1>
        <p style={{ color: 'var(--text-light)' }}>Nuk keni përfunduar akoma asnjë porosi.</p>
        <Link to="/shop" className="btn btn-primary">Vazhdo Blerjen</Link>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 80, minHeight: '100vh' }}>
      <div className="container-lg" style={{ paddingBottom: 40 }}>
        <div className="page-top">
          <div>
            <h1 style={{ fontSize: '1.6rem', marginBottom: 4 }}>Porositë e Mia</h1>
            <p style={{ color: 'var(--text-light)', fontSize: '.88rem' }}>{orders.length} porosi gjithsej</p>
          </div>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nr. Porosisë</th>
                  <th>Data</th>
                  <th>Totali</th>
                  <th>Pagesa</th>
                  <th>Statusi</th>
                  <th>Veprime</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id}>
                    <td>
                      <code style={{ fontSize: '.8rem', background: 'var(--cream)', padding: '2px 8px', borderRadius: 4 }}>
                        {o.order_number}
                      </code>
                    </td>
                    <td style={{ fontSize: '.82rem' }}>
                      {new Date(o.created_at).toLocaleDateString('sq')}
                    </td>
                    <td style={{ fontWeight: 700 }}>
                      ${(+o.total).toFixed(2)}
                    </td>
                    <td>
                      <span className={`badge ${o.payment_status === 'paid' ? 'badge-green' : 'badge-gold'}`}>
                        {o.payment_status === 'paid' ? 'Paguar' : 'Pa paguar'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${BADGE_MAP[o.status]}`}>
                        {STATUS_ALB[o.status]}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setSelectedOrder(o)}
                      >
                        <Eye size={14} /> Shiko
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Order detail modal */}
      {selectedOrder && (
        <div
          className="modal-overlay"
          onClick={e => e.target === e.currentTarget && setSelectedOrder(null)}
        >
          <div className="modal-box">
            <div className="modal-header">
              <h2 style={{ fontSize: '1.1rem' }}>Porosia {selectedOrder.order_number}</h2>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setSelectedOrder(null)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              {/* Shipping Address and Order Details */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                <div className="card" style={{ padding: 16 }}>
                  <div
                    style={{
                      fontSize: '.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '.06em',
                      color: 'var(--text-light)',
                      marginBottom: 10
                    }}
                  >
                    Adresa e Dërgimit
                  </div>
                  {(() => {
                    try {
                      const a =
                        typeof selectedOrder.shipping_address === 'string'
                          ? JSON.parse(selectedOrder.shipping_address)
                          : selectedOrder.shipping_address;
                      return (
                        <div style={{ fontSize: '.88rem' }}>
                          <div>{a?.full_name}</div>
                          <div>{a?.street}</div>
                          <div>
                            {a?.city}, {a?.country}
                          </div>
                        </div>
                      );
                    } catch {
                      return <div>N/A</div>;
                    }
                  })()}
                </div>
                <div className="card" style={{ padding: 16 }}>
                  <div
                    style={{
                      fontSize: '.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '.06em',
                      color: 'var(--text-light)',
                      marginBottom: 10
                    }}
                  >
                    Detajet
                  </div>
                  <div style={{ fontSize: '.88rem', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div>
                      Nëntotali: <strong>${(+selectedOrder.subtotal).toFixed(2)}</strong>
                    </div>
                    {selectedOrder.discount > 0 && (
                      <div style={{ color: 'var(--success)' }}>
                        Zbritje: -${(+selectedOrder.discount).toFixed(2)}
                      </div>
                    )}
                    <div>
                      Dërgim: <strong>${(+selectedOrder.shipping_cost).toFixed(2)}</strong>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '1rem' }}>
                      Totali: ${(+selectedOrder.total).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="card" style={{ padding: 16, marginBottom: 20 }}>
                <div
                  style={{
                    fontSize: '.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '.06em',
                    color: 'var(--text-light)',
                    marginBottom: 12
                  }}
                >
                  Produktet e Porositur
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {selectedOrder.items && selectedOrder.items.length > 0 ? (
                    selectedOrder.items.map((item, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          gap: 12,
                          paddingBottom: 12,
                          borderBottom: idx < selectedOrder.items.length - 1 ? '1px solid var(--cream-dark)' : 'none'
                        }}
                      >
                        {item.image && (
                          <img
                            src={item.image}
                            alt={item.name}
                            style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6 }}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500, marginBottom: 4 }}>{item.name}</div>
                          <div style={{ fontSize: '.82rem', color: 'var(--text-light)' }}>
                            {item.qty} x ${(+item.price).toFixed(2)} = ${(+item.price * +item.qty).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ color: 'var(--text-light)', fontSize: '.88rem' }}>Nuk ka produkte në këtë porosi</div>
                  )}
                </div>
              </div>

              {/* Status Info */}
              <div className="card" style={{ padding: 16, background: 'var(--cream)' }}>
                <div style={{ fontSize: '.88rem' }}>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ color: 'var(--text-light)' }}>Statusi: </span>
                    <span className={`badge ${BADGE_MAP[selectedOrder.status]}`}>
                      {STATUS_ALB[selectedOrder.status]}
                    </span>
                  </div>
                  {selectedOrder.tracking_number && (
                    <div>
                      <span style={{ color: 'var(--text-light)' }}>Nr. Gjurmimit: </span>
                      <code style={{ background: 'white', padding: '2px 6px', borderRadius: 3 }}>
                        {selectedOrder.tracking_number}
                      </code>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setSelectedOrder(null)}>
                Mbyll
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .page-top { display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;flex-wrap:wrap;gap:16px; }
        .modal-overlay { position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:2000;display:flex;align-items:center;justify-content:center;padding:20px;backdrop-filter:blur(4px); }
        .modal-box { background:white;border-radius:var(--radius-xl);width:min(620px,100%);max-height:90vh;display:flex;flex-direction:column;animation:modalIn .2s ease; }
        @keyframes modalIn { from{opacity:0;transform:scale(.96)} to{opacity:1;transform:scale(1)} }
        .modal-header { display:flex;justify-content:space-between;align-items:center;padding:20px 24px;border-bottom:1px solid var(--cream-dark); }
        .modal-body { padding:24px;overflow-y:auto; }
        .modal-footer { display:flex;justify-content:flex-end;gap:12px;padding:16px 24px;border-top:1px solid var(--cream-dark); }
      `}</style>
    </div>
  );
}
